import { makeAutoObservable, runInAction, toJS } from 'mobx'
import { newDefaultRecipe, type Recipe } from '../components/actions2/recipe/util'
import { resolveDefaultActionParams } from '../components/actions2/useRecipe'
import { MerchJarAPI, MerchJarPublicRecipesAPI } from '../pages/recipe_detail'
import { type ActionParams, hasExtraProperty } from '../types/constants'
import { FrequenciesList, UpdateReportAdTypes } from '../util/contstants'
import { ActionsList } from '../util/contstants'
import type { SelectBoxOption } from '../components/SelectBoxes'
import { UpdateReportAdTypesE } from '../constants'
import {
    ChangeBid,
    ChangeDailyBudgetAction,
    ChangeDefaultBidAction,
    ChangeBidAdjustmentAction,
    ChangeBiddingStrategyAction,
    CreateExactMatchNegativeAction,
    SetStateAction,
} from '../components/actions2/Actions'
import { capitalCase } from 'change-case'
import { type UserStore, userStore as defaultUserStore } from './user_store'
import { clone, cloneDeep } from 'lodash'
import { scheduledSegmentsApi, scheduledSegmentsApiError } from '../features/scheduled-segments/api'
import type {
    BigintId,
    RecipeSchedule,
    ScheduleDraft,
    ScheduleDetails,
    SchedulePreview,
    SchedulePreviewRequest,
    ScheduleOccurrence,
    ScheduleTimezones,
} from '../features/scheduled-segments/types'
import { scheduleDraftFromSchedule } from '../features/scheduled-segments/types'

const DEFAULT_VERSION = 1

interface ScheduleWriteToken {
    /** The exact local draft that was sent to the schedule service. */
    draftRevision: number
    /** Monotonic ordering for overlapping save responses. */
    generation: number
    /** Reject a response that belongs to an abandoned route/context. */
    contextGeneration: number
}

export class RecipeStore {
    recipe: Recipe
    loading: boolean = false
    // Keep route IDs as strings after v2 creates. Recipe and profile IDs can
    // exceed JavaScript's safe integer range, and schedule endpoints require
    // their decimal representation verbatim.
    recipeId: number | string | 'new' = 'new'
    version: number = DEFAULT_VERSION
    asPublicRecipe: boolean = false
    userStore: UserStore
    variables: Record<
        string,
        {
            type: string
            name: string
            formula: string
        }
    > = {}
    previewStats: {
        total: number | null
        loading: boolean
        error: string | null
    } = { total: 0, loading: false, error: null }
    pendingRefresh: boolean = false
    private loadGeneration: number = 0
    schedule: RecipeSchedule | null = null
    originalSchedule: RecipeSchedule | null = null
    scheduleDraft: ScheduleDraft | null = null
    scheduleOccurrences: ScheduleOccurrence[] = []
    scheduleMaxRunsPerDay: number | null = null
    scheduleTimezones: ScheduleTimezones | null = null
    scheduleLoading: boolean = false
    schedulePreviewLoading: boolean = false
    schedulePreviewValid: boolean = false
    scheduleHasNearby: boolean = false
    scheduleError: string | null = null
    schedulePaused: boolean = false
    /** The ScheduleEditor owns syntactic validation before the server preview. */
    scheduleEditorValid: boolean = true
    /** Enabled state last persisted by the recipe endpoint, before local edits. */
    originalEnabled: boolean = false
    private scheduleLoadGeneration: number = 0
    private schedulePreviewGeneration: number = 0
    private scheduleHydrationGeneration: number = 0
    private scheduleContextGeneration: number = 0
    private scheduleDraftRevisionValue: number = 0
    private scheduleWriteGeneration: number = 0
    private lastAppliedScheduleWriteGeneration: number = 0
    private scheduleProfileId: BigintId | null = null
    private scheduleRecipeId: BigintId | null = null

    constructor(userStore: UserStore | null = null) {
        this.recipe = newDefaultRecipe(DEFAULT_VERSION)
        makeAutoObservable(this)
        this.userStore = userStore ?? defaultUserStore
    }

    init() {
        this.loadGeneration += 1
        this.recipe = newDefaultRecipe(DEFAULT_VERSION)
        this.loading = false
        this.recipeId = 'new'
        this.version = DEFAULT_VERSION
        this.asPublicRecipe = false
        this.variables = {}
        this.previewStats = { total: 0, loading: false, error: null }
        this.resetSchedule()
    }

    async load(force: boolean = false) {
        if (this.recipeId === 'new') {
            return
        }
        if (!force && (this.loading || this.recipe.id)) {
            return
        }

        const API = this.asPublicRecipe ? MerchJarPublicRecipesAPI : MerchJarAPI

        runInAction(() => {
            this.loading = true
        })
        const loadGeneration = ++this.loadGeneration
        const promise = await API.Recipes.Read(this.recipeId as number, this.version)
        runInAction(() => {
            if (loadGeneration !== this.loadGeneration) {
                return
            }
            this.setRecipe(promise.data)
        })

        return loadGeneration === this.loadGeneration ? promise.data : undefined
    }

    setVersion(version: number) {
        this.setRecipe(newDefaultRecipe(version))
    }

    setRecipeId(recipeId: number | string | 'new') {
        if (this.recipeId !== recipeId) {
            this.loadGeneration += 1
        }
        this.recipeId = recipeId
    }

    setRecipe(recipe: Recipe) {
        // Recipe responses can serialize this two-value enum as a string.
        // Normalize it at the API boundary so downstream V2 capability checks
        // do not disagree with the rest of the UI's version handling.
        const rawVersion = recipe.version as unknown
        const version = rawVersion === '1' ? 1 : rawVersion === '2' ? 2 : rawVersion
        if (typeof version !== 'number') {
            throw new Error(`Invalid recipe version: ${String(rawVersion)}`)
        }

        this.version = version
        this.loading = false
        // V2 writes accept an object or null, while legacy JSON rows can still
        // deserialize an empty JSON array. Normalize at the read boundary so a
        // subsequent save never forwards that invalid shape to the V2 API.
        const frequencyParams = version === 2 && Array.isArray(recipe.frequency_params)
            ? null
            : recipe.frequency_params
        this.recipe = { ...recipe, version, frequency_params: frequencyParams }
        this.schedulePaused = recipe.paused_at != null
        this.originalEnabled = recipe.enabled
    }

    /** Record only state the recipe API has successfully accepted. */
    markRecipeStatePersisted(enabled: boolean) {
        this.originalEnabled = enabled
    }

    /** Clear schedule state and invalidate any in-flight schedule request. */
    resetSchedule() {
        this.scheduleLoadGeneration += 1
        this.schedulePreviewGeneration += 1
        this.scheduleHydrationGeneration += 1
        this.scheduleContextGeneration += 1
        this.scheduleDraftRevisionValue += 1
        this.scheduleProfileId = null
        this.scheduleRecipeId = null
        this.schedule = null
        this.originalSchedule = null
        this.scheduleDraft = null
        this.scheduleOccurrences = []
        this.scheduleMaxRunsPerDay = null
        this.scheduleTimezones = null
        this.scheduleLoading = false
        this.schedulePreviewLoading = false
        this.scheduleHasNearby = false
        this.schedulePreviewValid = false
        this.scheduleError = null
        this.schedulePaused = false
        this.scheduleEditorValid = true
    }

    setScheduleDraft(draft: ScheduleDraft | null) {
        const nextDraft = draft ? cloneDeep(draft) : null
        if (JSON.stringify(this.scheduleDraft) !== JSON.stringify(nextDraft)) {
            this.scheduleDraftRevisionValue += 1
        }
        this.schedulePreviewGeneration += 1
        this.scheduleDraft = nextDraft
        this.schedulePreviewLoading = false
        this.schedulePreviewValid = false
        this.scheduleHasNearby = false
        this.scheduleError = null
        // The editor reports the definitive local validity immediately after
        // this change. Until then, do not let a previous valid draft enable
        // Save for new malformed text.
        this.scheduleEditorValid = draft === null
    }

    resetScheduleDraft() {
        this.scheduleDraftRevisionValue += 1
        this.schedulePreviewGeneration += 1
        this.scheduleDraft = this.originalSchedule
            ? scheduleDraftFromSchedule(this.originalSchedule)
            : null
        this.schedulePreviewValid = this.scheduleDraft !== null
        this.schedulePreviewLoading = false
        this.scheduleHasNearby = false
        this.scheduleError = null
        this.scheduleEditorValid = true
    }

    setSchedulePaused(paused: boolean) {
        this.schedulePaused = paused
    }

    setScheduleEditorValid(valid: boolean) {
        this.scheduleEditorValid = valid
    }

    setScheduleError(error: string | null) {
        this.scheduleError = error
    }

    /** Establish an opaque-ID schedule context without replacing the user's draft. */
    setScheduleContext(profileId: BigintId, recipeId: BigintId | null) {
        this.scheduleLoadGeneration += 1
        this.schedulePreviewGeneration += 1
        this.scheduleHydrationGeneration += 1
        this.scheduleContextGeneration += 1
        this.scheduleProfileId = profileId
        this.scheduleRecipeId = recipeId
        this.scheduleLoading = false
        this.schedulePreviewLoading = false
        this.scheduleHasNearby = false
    }

    /**
     * Public read-only revision used to bind a schedule PUT response to the
     * exact draft that started it. A replacement-route hydration must never
     * overwrite a later local edit.
     */
    get scheduleDraftRevision(): number {
        return this.scheduleDraftRevisionValue
    }

    get scheduleDirty(): boolean {
        const original = this.originalSchedule
            ? scheduleDraftFromSchedule(this.originalSchedule)
            : null
        return JSON.stringify(this.scheduleDraft) !== JSON.stringify(original)
    }

    async loadSchedule(profileId: BigintId, recipeId: BigintId) {
        if (profileId !== this.scheduleProfileId || recipeId !== this.scheduleRecipeId) {
            this.scheduleContextGeneration += 1
        }
        const generation = ++this.scheduleLoadGeneration
        const hydrationGeneration = this.scheduleHydrationGeneration
        const draftRevision = this.scheduleDraftRevisionValue
        // A new segment has no original schedule, so any non-null draft is
        // necessarily local. Existing segments can also have a local delete
        // (null) or edit; both must survive the slow route hydration.
        const preserveLocalDraft = this.scheduleDirty
        this.scheduleProfileId = profileId
        this.scheduleRecipeId = recipeId
        this.scheduleLoading = true
        this.scheduleError = null

        try {
            const details = await scheduledSegmentsApi.get(profileId, recipeId)
            runInAction(() => {
                if (
                    generation !== this.scheduleLoadGeneration ||
                    hydrationGeneration !== this.scheduleHydrationGeneration ||
                    profileId !== this.scheduleProfileId ||
                    recipeId !== this.scheduleRecipeId
                ) {
                    return
                }
                this.schedule = details?.schedule ? cloneDeep(details.schedule) : null
                this.originalSchedule = details?.schedule ? cloneDeep(details.schedule) : null
                if (!preserveLocalDraft && draftRevision === this.scheduleDraftRevisionValue) {
                    const draft = details?.schedule
                        ? scheduleDraftFromSchedule(details.schedule)
                        : null
                    this.scheduleDraftRevisionValue += 1
                    this.scheduleDraft = draft ? cloneDeep(draft) : null
                    this.scheduleOccurrences = details?.occurrences ?? []
                    this.scheduleMaxRunsPerDay = details?.max_runs_per_day ?? null
                    this.schedulePreviewValid = details?.schedule != null
                    this.schedulePreviewLoading = false
                    this.scheduleHasNearby = details?.has_nearby_schedule ?? false
                    this.scheduleEditorValid = true
                }
                this.scheduleLoading = false
            })
            return generation === this.scheduleLoadGeneration &&
                hydrationGeneration === this.scheduleHydrationGeneration
                ? details
                : undefined
        } catch (error) {
            runInAction(() => {
                if (
                    generation !== this.scheduleLoadGeneration ||
                    hydrationGeneration !== this.scheduleHydrationGeneration ||
                    profileId !== this.scheduleProfileId ||
                    recipeId !== this.scheduleRecipeId
                ) {
                    return
                }
                this.scheduleLoading = false
                this.scheduleError = scheduledSegmentsApiError(error)
            })
            return undefined
        }
    }

    async loadScheduleTimezones(profileId: BigintId, recipeId: BigintId) {
        const generation = this.scheduleLoadGeneration
        try {
            const timezones = await scheduledSegmentsApi.timezones(profileId, recipeId)
            runInAction(() => {
                if (
                    generation !== this.scheduleLoadGeneration ||
                    profileId !== this.scheduleProfileId ||
                    recipeId !== this.scheduleRecipeId
                ) {
                    return
                }
                this.scheduleTimezones = timezones
            })
            return generation === this.scheduleLoadGeneration ? timezones : undefined
        } catch (error) {
            runInAction(() => {
                if (generation === this.scheduleLoadGeneration) {
                    this.scheduleError = scheduledSegmentsApiError(error)
                }
            })
            return undefined
        }
    }

    async loadScheduleDraftTimezones(profileId: BigintId) {
        const generation = this.scheduleLoadGeneration
        try {
            const timezones = await scheduledSegmentsApi.timezonesDraft(profileId)
            runInAction(() => {
                if (
                    generation !== this.scheduleLoadGeneration ||
                    profileId !== this.scheduleProfileId ||
                    this.scheduleRecipeId !== null
                ) {
                    return
                }
                this.scheduleTimezones = timezones
            })
            return generation === this.scheduleLoadGeneration ? timezones : undefined
        } catch (error) {
            runInAction(() => {
                if (generation === this.scheduleLoadGeneration && this.scheduleRecipeId === null) {
                    this.scheduleError = scheduledSegmentsApiError(error)
                }
            })
            return undefined
        }
    }

    /**
     * The schedule service is the authority for cron validation and upcoming
     * run calculations. Generation/context checks ensure a slower response for
     * a prior route, profile, or draft cannot make the current Save button
     * valid.
     */
    async previewSchedule(draft = this.scheduleDraft) {
        if (!draft) {
            this.schedulePreviewValid = false
            return undefined
        }

        const profileId = this.scheduleProfileId
        const recipeId = this.scheduleRecipeId
        if (!profileId) {
            return undefined
        }

        const generation = ++this.schedulePreviewGeneration
        this.schedulePreviewLoading = true
        this.schedulePreviewValid = false
        this.scheduleError = null

        try {
            const preview = await scheduledSegmentsApi.previewDraft(
                profileId,
                draft as SchedulePreviewRequest,
                recipeId,
            )
            runInAction(() => {
                if (
                    generation !== this.schedulePreviewGeneration ||
                    profileId !== this.scheduleProfileId ||
                    recipeId !== this.scheduleRecipeId
                ) {
                    return
                }
                this.applySchedulePreview(preview)
                this.schedulePreviewLoading = false
                this.schedulePreviewValid = true
            })
            return generation === this.schedulePreviewGeneration ? preview : undefined
        } catch (error) {
            runInAction(() => {
                if (
                    generation !== this.schedulePreviewGeneration ||
                    profileId !== this.scheduleProfileId ||
                    recipeId !== this.scheduleRecipeId
                ) {
                    return
                }
                this.schedulePreviewLoading = false
                this.schedulePreviewValid = false
                this.scheduleError = scheduledSegmentsApiError(error)
            })
            return undefined
        }
    }

    applySchedulePreview(preview: SchedulePreview) {
        this.scheduleOccurrences = preview.occurrences
        this.scheduleMaxRunsPerDay = preview.max_runs_per_day
        this.scheduleHasNearby = preview.has_nearby_schedule
    }

    /** Start an ordered schedule write for the current route/context. */
    beginScheduleWrite(draftRevision = this.scheduleDraftRevisionValue): ScheduleWriteToken {
        return {
            draftRevision,
            generation: ++this.scheduleWriteGeneration,
            contextGeneration: this.scheduleContextGeneration,
        }
    }

    applySavedSchedule(
        details: Omit<ScheduleDetails, 'schedule'> & { schedule: RecipeSchedule | null },
        writeToken?: ScheduleWriteToken,
    ): boolean {
        if (
            writeToken &&
            (writeToken.contextGeneration !== this.scheduleContextGeneration ||
                writeToken.generation < this.lastAppliedScheduleWriteGeneration)
        ) {
            return false
        }
        if (writeToken) {
            this.lastAppliedScheduleWriteGeneration = writeToken.generation
        }

        // A successful PUT/DELETE is newer than any route GET that began
        // before it. Ignore that stale hydration rather than letting it put an
        // old server copy (or null) back into the editor.
        this.scheduleHydrationGeneration += 1
        this.schedule = details.schedule ? cloneDeep(details.schedule) : null
        this.originalSchedule = details.schedule ? cloneDeep(details.schedule) : null
        const applyToCurrentDraft =
            !writeToken || writeToken.draftRevision === this.scheduleDraftRevisionValue
        if (applyToCurrentDraft) {
            const draft = details.schedule ? scheduleDraftFromSchedule(details.schedule) : null
            this.scheduleDraftRevisionValue += 1
            this.schedulePreviewGeneration += 1
            this.scheduleDraft = draft ? cloneDeep(draft) : null
            this.scheduleOccurrences = details.occurrences ?? []
            this.scheduleMaxRunsPerDay = details.max_runs_per_day ?? null
            this.schedulePreviewValid = this.scheduleDraft !== null
            this.schedulePreviewLoading = false
            this.scheduleHasNearby = details.has_nearby_schedule ?? false
            this.scheduleEditorValid = true
        }
        this.scheduleLoading = false
        this.scheduleError = null
        return applyToCurrentDraft
    }

    setName(name: string) {
        this.recipe.name = name
    }

    setEnabled(enabled: boolean) {
        this.recipe.enabled = enabled
    }

    setTrigger(trigger: string) {
        this.recipe.trigger = trigger
    }

    setAdType(adType: number) {
        // Check for invalid action/params combination and fix it
        if (
            this.recipe.action === ActionsList.SetBid &&
            hasExtraProperty(this.recipe.action_params) &&
            typeof this.recipe.action_params.extra === 'number'
        ) {
            // Fix the invalid state by setting proper bid defaults
            this.recipe.action_params = resolveDefaultActionParams(ActionsList.SetBid)
        }

        // If we currently have an action selected that doesn't exist on the new
        // ad type, then set the new action to something valid
        let a = this.recipe.action
        let actionParams = cloneDeep(this.recipe.action_params)

        if (
            adType !== this.recipe.ad_type &&
            (this.recipe.action === ActionsList.SetDailyBudget ||
                this.recipe.action === ActionsList.SetDefaultBid ||
                this.recipe.action === ActionsList.SetBid ||
                this.recipe.action === ActionsList.SetState ||
                this.recipe.action === ActionsList.CreateExactMatchNegative ||
                this.recipe.action === ActionsList.ChangeBiddingStrategy ||
                this.recipe.action === ActionsList.ChangeBidAdjustment)
        ) {
            switch (adType) {
                case UpdateReportAdTypes.Campaigns:
                    a = ActionsList.SetDailyBudget
                    actionParams = resolveDefaultActionParams(ActionsList.SetDailyBudget)
                    break
                case UpdateReportAdTypes.AdGroups:
                    a = ActionsList.SetDefaultBid
                    actionParams = resolveDefaultActionParams(ActionsList.SetDefaultBid)
                    break
                case UpdateReportAdTypes.Keywords:
                case UpdateReportAdTypes.Targets:
                case UpdateReportAdTypes.KeywordsAndTargets:
                    a = ActionsList.SetBid

                    // If we're switching TO SetBid from a different action, or if the params are invalid for SetBid
                    if (
                        this.recipe.action !== ActionsList.SetBid ||
                        (hasExtraProperty(actionParams) && typeof actionParams.extra === 'number')
                    ) {
                        // Set proper SetBid defaults when switching from different action or fixing invalid params
                        actionParams = resolveDefaultActionParams(ActionsList.SetBid)
                    } else {
                        // Preserve existing SetBid params when switching between Keywords and Targets
                        actionParams.type = ActionsList.SetBid
                    }
                    break
                case UpdateReportAdTypes.ProductAds:
                    a = ActionsList.SetState
                    actionParams = resolveDefaultActionParams(ActionsList.SetState)
                    break
                case UpdateReportAdTypes.SearchTerms:
                    a = ActionsList.CreateExactMatchNegative
                    actionParams = {
                        type: ActionsList.CreateExactMatchNegative,
                    }
                    break
                default:
                    console.log('setting default? with ad type', adType)
            }
        }

        this.recipe = {
            ...this.recipe,
            ad_type: adType,
            action: a,
            action_params: actionParams,
        }
    }

    setAction(action: number, actionParams: ActionParams) {
        if (action !== this.recipe.action) {
            // Only resolve defaults if actionParams is incomplete
            // For actions that need extra data, check if it's missing
            const needsExtraData =
                action === ActionsList.SetBid ||
                action === ActionsList.SetDefaultBid ||
                action === ActionsList.SetDailyBudget ||
                action === ActionsList.SetState ||
                action === ActionsList.ChangeBiddingStrategy ||
                action === ActionsList.ChangeBidAdjustment

            const hasExtra = 'extra' in actionParams && actionParams.extra

            if (needsExtraData && !hasExtra) {
                actionParams = resolveDefaultActionParams(action)
            }
        }

        this.recipe.action = action
        this.recipe.action_params = actionParams
    }

    setFrequency(frequency: number, frequencyParams: any) {
        this.recipe.frequency = frequency
        this.recipe.frequency_params = frequencyParams

        if (typeof frequencyParams === 'undefined') {
            switch (frequency) {
                case FrequenciesList.OnCertainDays:
                    frequencyParams = {
                        frequency: FrequenciesList.AfterEveryDataSync,
                        days: ['sunday'],
                    }
                    break
                case FrequenciesList.OnCertainDates:
                    frequencyParams = {
                        frequency: FrequenciesList.AfterEveryDataSync,
                        date: new Date(),
                        repeat: 'monthly',
                    }
                    break
            }
        }

        this.recipe.frequency = frequency
        this.recipe.frequency_params = frequencyParams
    }

    setVariables(
        variables: Record<
            string,
            {
                type: string
                name: string
                formula: string
            }
        >,
    ) {
        this.variables = variables ?? {}
    }

    setPreviewStats(stats: { total: number | null; loading: boolean; error: string | null }) {
        this.previewStats = stats
    }

    setPendingRefresh(pending: boolean) {
        this.pendingRefresh = pending
    }

    get availableAdTypes(): SelectBoxOption[] {
        const adTypes = [
            {
                name: 'Campaigns',
                id: UpdateReportAdTypes.Campaigns,
            },
            {
                name: 'Ad Groups',
                id: UpdateReportAdTypes.AdGroups,
            },
            {
                name: 'Keywords',
                id: UpdateReportAdTypes.Keywords,
            },
            {
                name: 'Targets',
                id: UpdateReportAdTypes.Targets,
            },
            {
                name: 'Keywords & Targets',
                id: UpdateReportAdTypes.KeywordsAndTargets,
            },
            {
                name: 'Product Ads',
                id: UpdateReportAdTypes.ProductAds,
            },
            {
                name: 'Search Terms',
                id: UpdateReportAdTypes.SearchTerms,
            },
        ]

        const mapToSelectBoxOption = (adType) => ({
            label: capitalCase(adType.name),
            value: adType.id,
            disabled:
                (this.version === 1 && adType.name === 'Keywords & Targets') ||
                (this.version === 1 &&
                    !this.userStore.isEligibleForProductAdRecipes &&
                    adType.name === 'Product Ads') ||
                (this.version === 1 && adType.name === 'Search Terms'),
        })

        return adTypes.map(mapToSelectBoxOption)
    }

    get availableActions(): SelectBoxOption[] {
        switch (this.recipe.ad_type) {
            case UpdateReportAdTypesE.Campaigns:
                return [SetStateAction, ChangeDailyBudgetAction, ChangeBiddingStrategyAction, ChangeBidAdjustmentAction]
            case UpdateReportAdTypesE.AdGroups:
                return [SetStateAction, ChangeDefaultBidAction]
            case UpdateReportAdTypesE.Keywords:
            case UpdateReportAdTypesE.Targets:
            case UpdateReportAdTypesE.KeywordsAndTargets:
                return [SetStateAction, ChangeBid]
            case UpdateReportAdTypesE.ProductAds:
                return [SetStateAction]
            case UpdateReportAdTypesE.SearchTerms:
                return [CreateExactMatchNegativeAction]
            default:
                console.error('Unknown ad type', this.recipe.ad_type)
                return []
        }
    }

    get showBidPreview(): boolean {
        if (this.version === 1) {
            return false
        }

        const allowedAdTypes = [4, 5, 999] // kws, targets, kws & targets
        if (!allowedAdTypes.includes(this.recipe.ad_type)) {
            return false
        }

        // Change bid
        if (this.recipe.action_params.type != 4) {
            return false
        }

        return true
    }

    get showDefaultBidPreview(): boolean {
        if (this.version === 1) {
            return false
        }

        const allowedAdTypes = [2] // ad groups
        if (!allowedAdTypes.includes(this.recipe.ad_type)) {
            return false
        }

        // Change default bid
        if (this.recipe.action_params.type != 3) {
            return false
        }

        return true
    }

    get showBudgetPreview(): boolean {
        if (this.version === 1) {
            return false
        }

        const allowedAdTypes = [1] // campaigns
        if (!allowedAdTypes.includes(this.recipe.ad_type)) {
            return false
        }

        // Change budget
        if (this.recipe.action_params.type != 2) {
            return false
        }

        return true
    }
}

export const recipeStore = new RecipeStore()
